// src/components/UsersMixedChart.tsx
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
  Skeleton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery } from "@tanstack/react-query";
import axios from "../utils/axios-instance";
import BaseComboChart from "./Base/BaseComboChart";
import { MixedChartData, NewUsersStats } from "../types/UsersStats";

// Fetch returns UsersStats
const fetchUserStats = async (startDate: string, endDate: string) => {
  const query = new URLSearchParams();
  query.append("startDate", startDate);
  query.append("endDate", endDate);
  const response = await axios.get<NewUsersStats>(
    `/admin/users/stats?${query.toString()}`,
  );
  return response.data;
};

export default function UsersMixedChart() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const MAX_RANGE_DAYS = isMobile ? 7 : 14;

  // Picker dates
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(6, "day"),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  // Applied dates
  const [appliedStartDate, setAppliedStartDate] = useState<Dayjs | null>(
    dayjs().subtract(6, "day"),
  );
  const [appliedEndDate, setAppliedEndDate] = useState<Dayjs | null>(dayjs());

  const toIsoDate = (date: Dayjs | null) =>
    date ? date.format("YYYY-MM-DD") : undefined;

  const {
    data: stats,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<NewUsersStats, Error>({
    queryKey: [
      "users-stats",
      appliedStartDate?.format(),
      appliedEndDate?.format(),
    ],
    queryFn: () => {
      const start = toIsoDate(appliedStartDate)!;
      const end = toIsoDate(appliedEndDate)!;
      return fetchUserStats(start, end);
    },
    enabled: !!appliedStartDate && !!appliedEndDate,
    refetchOnWindowFocus: false,
  });

  const comboData = useMemo(() => {
    const raw = (stats as any)?.data ?? stats;

    if (
      raw &&
      "bar" in raw &&
      "line" in raw &&
      Array.isArray(raw.bar?.data) &&
      Array.isArray(raw.line?.data)
    ) {
      return raw as MixedChartData;
    }
    return null;
  }, [stats]);

  // Validate date range
  const dateRangeValid = useMemo(() => {
    if (!startDate || !endDate) return true;
    const diff = endDate.diff(startDate, "day");
    return diff >= 0 && diff <= MAX_RANGE_DAYS;
  }, [startDate, endDate, MAX_RANGE_DAYS]);

  // Handlers
  const handleApply = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
  };

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        User Registration Analytics
      </Typography>

      <Stack direction={isMobile ? "column" : "row"} spacing={2} mb={3}>
        <DatePicker
          label="Start Date"
          value={startDate}
          onChange={(date) => setStartDate(date)}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <DatePicker
          label="End Date"
          value={endDate}
          onChange={(date) => setEndDate(date)}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!dateRangeValid}
          sx={{ height: "40px", minWidth: "80px" }}
        >
          Apply
        </Button>
        {!dateRangeValid && (
          <Typography
            variant="caption"
            color="error"
            sx={{ alignSelf: "center", ml: 1 }}
          >
            Max {MAX_RANGE_DAYS} days allowed
          </Typography>
        )}
        <Button variant="text" onClick={handleReset} sx={{ height: "40px" }}>
          Clear
        </Button>
      </Stack>

      {isLoading ? (
        <Box p={2}>
          <Skeleton variant="text" height={30} width="50%" />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </Box>
      ) : isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
          <Button size="small" onClick={() => refetch()} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      ) : comboData ? (
        <BaseComboChart data={comboData} />
      ) : (
        <Typography color="text.secondary">No data available.</Typography>
      )}
    </Box>
  );
}
